import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import api from '../services/api';
import './AdminGrowthGraphs.css';

const RANGE_OPTIONS = [
  { value: '30',  label: '30 days' },
  { value: '90',  label: '90 days' },
  { value: '365', label: '1 year'  },
];

const TOOLTIP_CONTENT_STYLE = {
  fontSize: '0.82rem',
  borderRadius: '6px',
  border: '1px solid #e0e0e0',
};

function formatXTick(val) {
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTooltipLabel(val) {
  return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function normalizeRows(arr) {
  return (arr || []).map(d => ({ date: d.date, count: Number(d.count) }));
}

function AdminGrowthGraphs({ setActiveFilter }) {
  const [usersList, setUsersList]         = useState([]);
  const [growthData, setGrowthData]       = useState(null);
  const [range, setRange]                 = useState('30');
  const [isLoadingUsers, setIsLoadingUsers]   = useState(true);
  const [isLoadingGrowth, setIsLoadingGrowth] = useState(true);
  const [errorUsers, setErrorUsers]       = useState(null);
  const [errorGrowth, setErrorGrowth]     = useState(null);

  useEffect(() => {
    api.get('/admin/users/list')
      .then(res => setUsersList(res.data.users || []))
      .catch(err => setErrorUsers(err.response?.status === 403 ? 'Access denied' : 'Failed to load user data'))
      .finally(() => setIsLoadingUsers(false));
  }, []);

  useEffect(() => {
    setIsLoadingGrowth(true);
    setErrorGrowth(null);
    api.get(`/admin/growth?range=${range}`)
      .then(res => {
        setGrowthData({
          signups_by_day:      normalizeRows(res.data.signups_by_day),
          active_users_by_day: normalizeRows(res.data.active_users_by_day),
          plants_added_by_day: normalizeRows(res.data.plants_added_by_day),
        });
      })
      .catch(err => setErrorGrowth(err.response?.status === 403 ? 'Access denied' : 'Failed to load growth data'))
      .finally(() => setIsLoadingGrowth(false));
  }, [range]);

  const totalUsers = usersList.length;
  const freeUsers  = usersList.filter(u => (u.subscription_tier || 'free').toLowerCase() === 'free').length;
  const proUsers   = usersList.filter(u => (u.subscription_tier || '').toLowerCase() === 'pro').length;
  const oldestUser = usersList.length
    ? usersList.reduce((oldest, u) => u.created_at < oldest.created_at ? u : oldest, usersList[0])
    : null;
  const longestAccountDays = oldestUser
    ? Math.floor((Date.now() - new Date(oldestUser.created_at).getTime()) / 86400000)
    : 0;

  const renderChart = (title, data) => {
    const allZero = !data || data.every(d => d.count === 0);
    return (
      <div className="agg-chart-section">
        <div className="agg-chart-title">{title}</div>
        {allZero ? (
          <div className="agg-no-data">No data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatXTick}
                tick={{ fontSize: 11, fill: '#9e9e9e' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#9e9e9e' }}
                tickLine={false}
                axisLine={false}
                width={30}
              />
              <Tooltip
                labelFormatter={formatTooltipLabel}
                formatter={val => [val, 'Count']}
                contentStyle={TOOLTIP_CONTENT_STYLE}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#2e7d32"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#2e7d32' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  };

  return (
    <div className="agg-container">

      <div className="agg-summary-bar">
        {isLoadingUsers ? (
          <div className="agg-loading">Loading...</div>
        ) : errorUsers ? (
          <div className="agg-error">{errorUsers}</div>
        ) : (
          [
            { label: 'Total Users',     value: totalUsers,              onClick: () => setActiveFilter?.({ type: 'all',    value: null,           label: 'All users' }) },
            { label: 'Free Tier',       value: freeUsers,               onClick: () => setActiveFilter?.({ type: 'tier',   value: 'free',         label: 'Free tier users' }) },
            { label: 'Pro Tier',        value: proUsers,                onClick: () => setActiveFilter?.({ type: 'tier',   value: 'pro',          label: 'Pro tier users' }) },
            { label: 'Longest Account', value: `${longestAccountDays}d`, onClick: () => oldestUser && setActiveFilter?.({ type: 'userId', value: oldestUser.id, label: 'Longest account' }) },
          ].map(({ label, value, onClick }) => (
            <div key={label} className="agg-summary-cell agg-summary-cell--clickable" onClick={onClick}>
              <div className="agg-summary-value">{value}</div>
              <div className="agg-summary-label">{label}</div>
            </div>
          ))
        )}
      </div>

      <div className="agg-range-selector">
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`agg-range-btn${range === opt.value ? ' agg-range-btn--active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoadingGrowth ? (
        <div className="agg-loading">Loading charts...</div>
      ) : errorGrowth ? (
        <div className="agg-error">{errorGrowth}</div>
      ) : growthData && (
        <div className="agg-charts">
          {renderChart('New Signups',  growthData.signups_by_day)}
          {renderChart('Active Users', growthData.active_users_by_day)}
          {renderChart('Plants Added', growthData.plants_added_by_day)}
        </div>
      )}

    </div>
  );
}

export default AdminGrowthGraphs;
